import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, ShieldCheck, FileSearch, Award, Landmark, ShieldAlert, BadgeCheck, Check, Upload, Sparkles } from "lucide-react";
import { GOVT_PORTALS, WHAT_WE_CHECK, RATING_BANDS, WHO_ITS_FOR, PRICING_PLANS, VERIFICATION_FAQS, CONTACT } from "../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { LandRatingCard, SAMPLE_RATING } from "../components/LandRatingCard";
import { WhatsAppIcon } from "../components/SiteLayout";

export const SectionHeader = ({ eyebrow, title, sub, dark = false, className = "" }) => (
  <div className={`max-w-2xl ${className}`}>
    <p className="tt-eyebrow mb-4">{eyebrow}</p>
    <h2 className={`font-heading text-3xl md:text-5xl font-medium tracking-tight ${dark ? "text-white" : "text-[#113835]"}`}>{title}</h2>
    {sub && <p className={`mt-5 text-base md:text-lg font-light leading-relaxed ${dark ? "text-white/70" : "text-gray-600"}`}>{sub}</p>}
  </div>
);

export const CTABand = () => (
  <section className="bg-[#113835] tt-grain">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
      <div>
        <p className="tt-eyebrow mb-4">Begin Your Due Diligence</p>
        <h2 className="font-heading text-3xl md:text-5xl font-medium text-white tracking-tight max-w-2xl">
          Know what you're buying <span className="font-accent italic text-[#F4D387]">before</span> you commit.
        </h2>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 shrink-0">
        <Link to="/request-title-search" data-testid="cta-band-request" className="tt-btn-gold">
          Request Property Verification <ArrowRight size={16} strokeWidth={1.5} />
        </Link>
        <a
          href={`https://wa.me/91${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi Terraa & Title, I'd like to talk to an expert about a property.")}`}
          target="_blank" rel="noopener noreferrer"
          data-testid="cta-band-whatsapp" className="tt-btn-outline on-dark"
        >
          <WhatsAppIcon size={16} /> Talk to an Expert on WhatsApp
        </a>
      </div>
    </div>
  </section>
);

const STEPS = [
  { icon: Upload, title: "Submit the property", text: "Enter the address or survey number and upload the documents you already have." },
  { icon: FileSearch, title: "Expert review begins", text: "We review ownership, encumbrances, zoning, tax-related records, and document consistency using relevant sources and available case information." },
  { icon: Award, title: "Receive your report", text: "Get a structured PDF report with findings, key risks, and a Land Rating out of 100." },
];

const PROBLEM_STATS = [
  ["2 in 3", "civil cases in Indian courts are land & property disputes"],
  ["20+ yrs", "is how long a disputed title can drag through litigation"],
  ["1 document", "a missing Mother Deed or EC — is all it takes to lose everything"],
];

const WHY_TERRAA = [
  { icon: Landmark, title: "Source-based review", text: "We review relevant records and submitted documents to help identify ownership, encumbrance, and compliance issues — not broker claims." },
  { icon: ShieldAlert, title: "Clear red-flag reporting", text: "We make critical gaps visible so buyers can investigate further before payment or agreement. If the Mother Deed or EC is missing, the rating collapses below 30." },
  { icon: BadgeCheck, title: "Simple decision output", text: "A structured report and Land Rating help you quickly understand whether to proceed, pause, or investigate further." },
];

export default function HomePage() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/rating?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-24 md:pt-36 pb-16 md:pb-24 text-center">
          <p className="tt-eyebrow mb-6 tt-fade-up justify-center flex">Property Verification · Maharashtra</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#113835] tracking-tight leading-[1.1] tt-fade-up tt-delay-1">
            Verify land and property risk <span className="italic text-[#16324F]">before you buy.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-gray-500 font-light leading-relaxed max-w-2xl mx-auto tt-fade-up tt-delay-2">
            Get an expert-reviewed property verification report for plots, land parcels, and second homes in Maharashtra.
            We review ownership, encumbrances, zoning, tax compliance, and document consistency — summarized in a simple Land Rating out of 100.
          </p>

          <form onSubmit={onSearch} data-testid="home-search-form" className="mt-10 tt-fade-up tt-delay-3">
            <div className="flex flex-col sm:flex-row items-stretch max-w-2xl mx-auto border-2 border-[#113835] shadow-[0_18px_50px_rgba(17,56,53,0.12)] bg-white">
              <div className="flex items-center flex-1 px-5">
                <Search size={19} strokeWidth={1.5} className="text-[#C1A74A] shrink-0" />
                <input
                  data-testid="home-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Plot address, survey no., or locality in Maharashtra…"
                  className="w-full px-4 py-4 md:py-5 text-sm md:text-base text-[#113835] placeholder:text-gray-400 focus:outline-none font-light"
                  aria-label="Search a plot"
                />
              </div>
              <button
                type="submit"
                data-testid="home-search-submit"
                className="bg-[#113835] hover:bg-[#16324F] transition-colors text-[#F4D387] font-heading text-xs uppercase tracking-[0.18em] px-8 py-4 md:py-0 flex items-center justify-center gap-2"
              >
                Check Rating <ArrowRight size={14} strokeWidth={1.75} />
              </button>
            </div>
          </form>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 tt-fade-up tt-delay-3">
            <Link to="/request-title-search" data-testid="hero-cta-request" className="tt-btn-gold !py-3">
              Request Property Verification <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
            <Link to="/sample-report" data-testid="hero-cta-sample" className="tt-btn-outline !py-3">
              View Sample Report
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[0.7rem] uppercase tracking-[0.14em] font-heading text-gray-400 tt-fade-up tt-delay-3">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} strokeWidth={1.5} className="text-[#C1A74A]" /> Maharashtra-Focused</span>
            <span>Expert-Reviewed</span>
            <span>Score + PDF Report</span>
          </div>
        </div>
      </section>

      {/* Portals strip */}
      <section className="border-y border-[#113835]/8 bg-[#F9FAFA] py-6" data-testid="portals-strip">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-heading text-[0.66rem] uppercase tracking-[0.2em] text-[#113835]/70 mb-3">
            Relevant records reviewed across Maharashtra property workflows
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
            {GOVT_PORTALS.slice(0, 6).map((p) => (
              <span key={p.name} className="font-heading text-[0.66rem] uppercase tracking-[0.18em] text-[#1D504C]/60">{p.name}</span>
            ))}
          </div>
          <p className="mt-3 text-[0.62rem] text-gray-400 font-light">
            Checks depend on property type, location, record availability, and submitted documents.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-24 md:py-32 bg-[#113835] tt-grain" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div>
              <p className="tt-eyebrow mb-4">The Problem</p>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-white tracking-tight">
                Buying blind is one of the <span className="font-accent italic text-[#F4D387]">costliest mistakes</span> in real estate.
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/70 font-light leading-relaxed">
                Property buyers often rely on broker claims, incomplete paperwork, and fragmented records.
                Missing title history, hidden encumbrances, zoning issues, or unclear document trails can turn a purchase
                into a long legal and financial problem.
              </p>
              <p className="mt-5 text-base md:text-lg text-white/70 font-light leading-relaxed">
                <span className="text-[#F4D387]">Terraa & Title helps surface these risks before you commit.</span>
              </p>
            </div>
            <div className="grid gap-px bg-white/10 border border-white/10">
              {PROBLEM_STATS.map(([num, label]) => (
                <div key={num} className="bg-[#113835] p-8 md:p-10">
                  <p className="font-heading text-4xl md:text-5xl font-semibold text-[#F4D387] leading-none">{num}</p>
                  <p className="mt-3 text-sm md:text-base text-white/70 font-light leading-relaxed">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Terraa */}
      <section className="py-24 md:py-32 bg-white" data-testid="why-terraa-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Why Terraa & Title"
            title="Built to surface property risk early."
            sub="Anyone can print a title report. Ours is built to protect the buyer — reviewed at the source, honest about deal-breakers, and simple enough to act on."
            className="mb-16"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_TERRAA.map((w) => (
              <div key={w.title} className="border border-[#113835]/10 p-10 hover:border-[#C1A74A]/60 transition-colors">
                <w.icon size={28} strokeWidth={1.25} className="text-[#C1A74A]" />
                <h3 className="font-heading text-base font-medium text-[#113835] mt-6">{w.title}</h3>
                <p className="mt-3 text-sm text-gray-600 font-light leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terraa AI showcase */}
      <section className="py-24 md:py-32 bg-[#16324F] tt-grain" data-testid="terraa-ai-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="tt-eyebrow mb-4">New · Terraa AI</p>
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-white tracking-tight">
              Ask anything about Indian property. <span className="font-accent italic text-[#F4D387]">Get answers in seconds.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/70 font-light leading-relaxed">
              Titles, encumbrances, 7/12 extracts, agricultural land rules — Terraa AI explains it all in plain language.
              Attach a property document, even in Marathi or Hindi, and it reads it for you.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/ask-terraa" data-testid="home-ask-terraa-cta" className="tt-btn-gold">
                <Sparkles size={15} strokeWidth={1.5} /> Ask Terraa AI — Free
              </Link>
              <Link to="/ask-terraa?tab=scan" data-testid="home-scan-doc-cta" className="tt-btn-outline on-dark">
                Scan a Document (OCR + Map)
              </Link>
            </div>
          </div>
          <div className="bg-white border-2 border-[#C1A74A]/60 shadow-[0_24px_60px_rgba(0,0,0,0.25)]">
            <div className="bg-[#113835] px-6 py-3.5">
              <p className="font-heading text-[0.68rem] uppercase tracking-[0.2em] text-[#F4D387] flex items-center gap-2">
                <Sparkles size={13} /> Terraa AI · Property Assistant
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-end">
                <p className="bg-[#113835] text-white text-sm font-light px-4 py-3 max-w-[80%]">
                  The seller says the 7/12 extract is enough. Is that true?
                </p>
              </div>
              <div className="flex justify-start">
                <p className="bg-[#F4F6F5] text-[#113835] text-sm font-light px-4 py-3 max-w-[85%] leading-relaxed">
                  No — a 7/12 extract alone is not enough. It shows current holding, but not the ownership chain or
                  hidden loans. You should also verify the Mother Deed and a 30-year Encumbrance Certificate. If either
                  is missing, treat it as a deal-breaker until verified.
                </p>
              </div>
              <div className="flex justify-end">
                <p className="bg-[#113835] text-white text-sm font-light px-4 py-3 max-w-[80%]">
                  Can you read my sale deed? It's in Marathi.
                </p>
              </div>
              <div className="flex justify-start">
                <p className="bg-[#F4F6F5] text-[#113835] text-sm font-light px-4 py-3 max-w-[85%] leading-relaxed">
                  Yes — attach it here and I'll read it in Marathi and explain what it says, who the parties are, and
                  anything that looks off.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we check */}
      <section className="py-24 md:py-32 bg-[#F9FAFA]" data-testid="what-we-check-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="Scope of Review" title="What we review before you buy." className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#113835]/10 border border-[#113835]/10">
            {WHAT_WE_CHECK.map((item) => (
              <div key={item} className="bg-white p-7 flex items-start gap-3">
                <Check size={16} strokeWidth={2} className="text-[#C1A74A] mt-0.5 shrink-0" />
                <p className="text-sm text-[#113835] font-light leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="How It Works" title="From property details to a clear decision, in three steps." className="mb-16" />
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="border border-[#113835]/10 p-10 hover:border-[#C1A74A]/60 transition-colors">
                <div className="flex items-center gap-4">
                  <span className="font-accent italic text-4xl text-[#16324F]/30">0{i + 1}</span>
                  <s.icon size={26} strokeWidth={1.25} className="text-[#C1A74A]" />
                </div>
                <h3 className="font-heading text-base font-medium text-[#113835] mt-6">{s.title}</h3>
                <p className="mt-3 text-sm text-gray-600 font-light leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Understand your Land Rating */}
      <section className="py-24 md:py-32 bg-[#F9FAFA]" data-testid="sample-rating-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeader
              eyebrow="The Terraa Land Rating"
              title="Understand your Land Rating."
              sub="Each rating weighs title history, encumbrances, zoning verification, and tax compliance into one number out of 100."
            />
            <div className="mt-8 space-y-3" data-testid="rating-bands">
              {RATING_BANDS.map((b) => (
                <div key={b.range} className="flex items-center gap-4 border border-[#113835]/10 bg-white px-5 py-3">
                  <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: b.color }} />
                  <span className="font-heading text-sm font-semibold text-[#113835] w-24 shrink-0">{b.range}</span>
                  <span className="text-sm text-gray-600 font-light">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/sample-report" data-testid="home-sample-cta" className="tt-btn-gold">
                View Sample Report <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
              <Link to="/due-diligence-tracker" data-testid="home-tracker-link" className="tt-btn-outline">
                Free 10-Point Tracker
              </Link>
            </div>
          </div>
          <div className="pt-4">
            <LandRatingCard rating={SAMPLE_RATING} sample />
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-24 md:py-32 bg-white" data-testid="who-its-for-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="Who It's For" title="Built for serious property decisions." className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {WHO_ITS_FOR.map((w) => (
              <div key={w.title} className="border-l-2 border-[#C1A74A]/60 pl-8 py-2">
                <h3 className="font-heading text-lg font-medium text-[#113835]">{w.title}</h3>
                <p className="mt-3 text-sm text-gray-600 font-light leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-24 md:py-32 bg-[#113835] tt-grain" data-testid="pricing-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader dark eyebrow="Pricing" title="Simple pricing for due diligence." className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING_PLANS.map((p) => (
              <div key={p.id} className={`p-9 ${p.highlight ? "bg-white" : "bg-white/[0.04] border border-white/15"}`}>
                {p.highlight && <p className="font-heading text-[0.6rem] uppercase tracking-[0.22em] text-[#C1A74A] mb-3">Most Chosen</p>}
                <h3 className={`font-heading text-base font-medium ${p.highlight ? "text-[#113835]" : "text-white"}`}>{p.name}</h3>
                <p className={`font-heading text-3xl font-semibold mt-3 ${p.highlight ? "text-[#113835]" : "text-[#F4D387]"}`}>{p.price}</p>
                <p className={`text-xs mt-1 font-light ${p.highlight ? "text-gray-500" : "text-white/50"}`}>{p.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/pricing" data-testid="home-pricing-link" className="tt-btn-gold">
              View Full Pricing <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
            <Link to="/request-title-search" data-testid="home-pricing-request" className="tt-btn-outline on-dark">
              Request Verification
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <SectionHeader eyebrow="Common Questions" title="Answers, before you ask." />
            <Link to="/faqs" data-testid="home-faqs-link" className="tt-link font-heading text-xs uppercase tracking-[0.16em] text-[#1D504C] shrink-0 pb-1">
              View All FAQs
            </Link>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
            {VERIFICATION_FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#113835]/10">
                <AccordionTrigger className="font-heading text-left text-base font-medium text-[#113835] hover:text-[#C1A74A] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 font-light leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
